import { useAssetStalenessQuery } from '@verantos-dev/data-access-cohorts';

import { useAdRouteParams } from './useAdRouteParams';
import { useHydrateAnalyticalDatasetDefinition } from './useHydrateAnalyticalDatasetDefinition';
import { useAnalyticalDatasetProfileQuery } from '../api/useAnalyticalDatasetProfileQuery';
import { selectHasStagedChanges, selectLoadedVersionId } from '../state/selectors';
import { useAnalyticalDatasetDefinitionStore } from '../state/useAnalyticalDatasetDefinitionStore';

export interface UseAnalyticalDatasetDefinitionTabParams {
  /** TODO: cleanup with asset_edit_while_in_use ff — read once by the
   *  top-level `AnalyticalDatasetDefinitionTab` and passed down so this
   *  hook doesn't re-check the flag itself. */
  isAssetEditWhileInUseEnabled?: boolean;
}

export const useAnalyticalDatasetDefinitionTab = ({
  isAssetEditWhileInUseEnabled = false,
}: UseAnalyticalDatasetDefinitionTabParams = {}) => {
  const { projectId, analyticalDatasetProfileId, analyticalDatasetVersionId } = useAdRouteParams();

  // Seeds the store from the version (shared with the page-level hydration so a
  // Results-tab refresh hydrates too); returns the same deduped version query.
  const { versionQuery } = useHydrateAnalyticalDatasetDefinition();

  const profileQuery = useAnalyticalDatasetProfileQuery({ projectId, analyticalDatasetProfileId });

  const loadedVersionId = useAnalyticalDatasetDefinitionStore(selectLoadedVersionId);
  const hasStagedChanges = useAnalyticalDatasetDefinitionStore(selectHasStagedChanges);

  // "Loading" = the store hasn't reflected the URL's version yet. Covers
  // three windows the raw `versionQuery.isLoading` misses:
  // - Route params not yet ready (query disabled → `isLoading` is false).
  // - Query is in flight (isLoading true).
  // - Query succeeded but the seeding effect hasn't run yet (one paint gap).
  // - After save-navigate to a new versionId, until the new version seeds.
  const isLoading =
    versionQuery.isLoading ||
    !analyticalDatasetVersionId ||
    loadedVersionId !== analyticalDatasetVersionId;

  // Mirrors cohort's results-tab banner: once the dataset is PROCESSED, warn
  // that staged edits make the processed output stale vs the current definition.
  const showStagedChangesWarning =
    hasStagedChanges && profileQuery.data?.data.latestStatus === 'PROCESSED';

  // Server-computed — an analytical variable this dataset (directly
  // or via a source cohort) depends on has changed since this dataset was
  // last saved/processed. Gated on the caller-supplied flag so the query
  // doesn't fire at all while the flag is off. TODO: cleanup with
  // asset_edit_while_in_use ff
  const { data: staleness } = useAssetStalenessQuery({
    kind: 'analytical_dataset',
    id: analyticalDatasetVersionId,
    enabled: isAssetEditWhileInUseEnabled,
  });
  const showStaleAnalyticalVariablesWarning =
    isAssetEditWhileInUseEnabled && (staleness?.isStale ?? false);
  // Which of the AD's dependencies went stale — drives which warning copy is
  // shown (AV-only / cohort-only / both). Empty unless the banner is showing.
  const staleWarningReasons = showStaleAnalyticalVariablesWarning ? staleness?.reasons ?? [] : [];

  return {
    isLoading,
    showStagedChangesWarning,
    showStaleAnalyticalVariablesWarning,
    staleWarningReasons,
  };
};
